import React, { useCallback, useEffect, useState } from 'react';
import { Paperclip, Download, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import NoDataFound from '../../common/NoDataFound';
import { confirmDelete } from '../../common/confirm';
import {
  procedureEvidenceService,
  fileService,
  type ProcedureEvidence,
} from '../../../services/auditService';
import { useEntitySettings } from '../../../contexts/EntitySettingsContext';

/**
 * EvidenceModal — the per-question evidence popup shared by EVERY procedure body
 * (all 10 types). Opened by a row's paperclip. Lists all files attached to the
 * clicked question — scoped by (procedure_instance_id, item_type_id,
 * item_record_id) — with view + detach, plus the same upload control the Findings
 * detail modal uses. Persistence is the generic adt_procedure_evidence junction
 * via procedureEvidenceService; viewing a row opens it through fileService.open.
 *
 * The single-file evidence_file_id on each response row is independent of this —
 * this popup is the multi-file surface the auditor actually works in.
 */

export interface EvidenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  procedureInstanceId: number;
  /** Polymorphic question pointer — same ids each body already uses for findings. */
  itemTypeId: number | null;
  itemRecordId: number | null;
  entityId: number;
  currentUserId: number;
  /** Optional question text shown under the title for context. */
  questionLabel?: string | null;
  /** When true (e.g. Approved), uploading/detaching is blocked but viewing works. */
  readOnly?: boolean;
  /** Called after a successful upload or detach so the parent can refresh its inline evidence map. */
  onChanged?: () => void;
}

const EvidenceModal: React.FC<EvidenceModalProps> = ({
  isOpen,
  onClose,
  procedureInstanceId,
  itemTypeId,
  itemRecordId,
  entityId,
  currentUserId,
  questionLabel,
  readOnly = false,
  onChanged,
}) => {
  const { formatDateTime } = useEntitySettings();
  const [rows, setRows] = useState<ProcedureEvidence[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await procedureEvidenceService.list(procedureInstanceId, itemTypeId, itemRecordId);
      setRows(list);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load evidence.');
    } finally {
      setLoading(false);
    }
  }, [procedureInstanceId, itemTypeId, itemRecordId]);

  useEffect(() => {
    if (isOpen) reload();
    else { setRows([]); setError(null); setAttachOpen(false); setFile(null); }
  }, [isOpen, reload]);

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      const uploadFile = files?.[0];
      if (!uploadFile) return;
      setFile(uploadFile);
      setBusy(true);
      setError(null);
      try {
        const row = await procedureEvidenceService.upload(
          uploadFile, procedureInstanceId, itemTypeId, itemRecordId, entityId, currentUserId,
        );
        setRows((prev) => [row, ...prev]);
        onChanged?.();
        setFile(null);
        setAttachOpen(false);
      } catch (e: any) {
        setError(e?.response?.data?.detail ?? e?.message ?? 'Upload failed.');
      } finally {
        setBusy(false);
      }
    },
    [procedureInstanceId, itemTypeId, itemRecordId, entityId, currentUserId],
  );

  const handleDownload = useCallback(async (fileId: number, fileName?: string | null) => {
    setDownloadingId(fileId);
    setError(null);
    try {
      await fileService.download(fileId, fileName ?? 'evidence');
    } catch (e: any) {
      setError(e?.message ?? 'Could not download the file.');
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const handleDetach = useCallback(async (row: ProcedureEvidence) => {
    const name = row.file_name ?? `file #${row.file_id}`;
    const confirmed = await confirmDelete({ title: `Detach "${name}"?` });
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await procedureEvidenceService.remove(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      onChanged?.();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to detach.');
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_procedure_evidence">
      <Modal.Header onClose={onClose}>
        <span
          className="evidence-modal-title"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Paperclip size={16} /> Evidence{rows.length > 0 ? ` (${rows.length})` : ''}
        </span>
      </Modal.Header>
      <Modal.Body>
        {questionLabel && <p className="response-remarks-question">{questionLabel}</p>}

        {!readOnly && (
          <div>
            <button
              type="button"
              className="observation-evidence-toggle"
              onClick={() => setAttachOpen(v => !v)}
              disabled={busy}
            >
              <Paperclip size={13} />
              <span>Attach Evidence</span>
              {file && <span className="observation-evidence-badge">{file.name}</span>}
              {attachOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            {attachOpen && (
              <div className="observation-evidence-panel">
                <FormControl.FileUpload
                  data-testid="INP_procedure_evidence_upload"
                  onChange={handleUpload}
                  disabled={busy}
                  placeholder="Click to Upload File"
                  subtitle="or Drag & Drop"
                />
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="inline-error inline-error--inline" role="alert">{error}</div>
        )}

        {!loading && rows.length === 0 && (
          <NoDataFound title="No evidence" message="No files attached to this question yet." />
        )}

        {rows.length > 0 && (
          <table className="table-list-table" style={{ marginTop: 20 }}>
            <thead>
              <tr>
                <th className="table-list-th table-list-cell--sno">S.No</th>
                <th className="table-list-th">File</th>
                <th className="table-list-th">Attached</th>
                <th className="table-list-th" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id}>
                  <td className="table-list-cell table-list-cell--sno">{idx + 1}</td>
                  <td className="table-list-cell">{row.file_name ?? `#${row.file_id}`}</td>
                  <td className="table-list-cell">
                    {row.attached_on ? formatDateTime(row.attached_on) : '—'}
                  </td>
                  <td className="table-list-cell">
                    <div className="response-action-cluster">
                      <button
                        type="button"
                        data-testid={`BTN_procedure_evidence_download_${row.id}`}
                        className="finding-evidence-download-btn"
                        onClick={() => handleDownload(row.file_id, row.file_name)}
                        disabled={downloadingId === row.file_id}
                        title="Download"
                        aria-label="Download evidence"
                      >
                        <Download size={14} />
                      </button>
                      {!readOnly && (
                        <button
                          type="button"
                          data-testid={`BTN_procedure_evidence_detach_${row.id}`}
                          className="finding-evidence-detach-btn"
                          onClick={() => handleDetach(row)}
                          disabled={busy}
                          title="Detach"
                          aria-label="Detach evidence"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary-outline" onClick={onClose} data-testid="BTN_procedure_evidence_close">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EvidenceModal;
